import {
  EventLog_BookingKitEventFragment,
  useBookingKitEventLog_GetEventsQuery,
  BookingKitEventType
} from "@graphql";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { Box, Card } from "@material-ui/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { EventList } from "./components/event-list";
import { createFilteredEventComponents } from "./utils/create-filtered-event-components";

import { FilterType } from "./utils/filter-type";
import { groupEventsByFilterType } from "./utils/group-events-by-filter-type";
import {
  EventLogDataProvider,
  useEventLogData
} from "./utils/refetch-booking-kit-events";
import { useGlobalStore, useLocale } from "@hooks";
import { createInitialMainFilters } from "./utils/initial-filters";

type Props = {
  kitId: string;
};

const PAGE_SIZE = 20;

export const BookingKitEventLog = (props: Props) => {
  const snack = useSnackbarsPipeline();
  const { locale } = useLocale();
  const store = useGlobalStore();

  const [fetchMoreIsLoading, setFetchMoreIsLoading] = useState(false);

  const currentPage = useRef(1);

  const [eventsGroupedByFilterType, setEventsGroupedByFilterType] = useState<Record<
    FilterType,
    EventLog_BookingKitEventFragment[]
  > | null>(null);

  const [activeMainFilters] = useState(
    createInitialMainFilters(store.user.myProfile.AzureGroups)
  );

  const activeFilters = [...Object.entries(activeMainFilters)]
    .filter(([_, selected]) => selected)
    .map(([filter, _]) => filter as FilterType);

  useEffect(() => {
    currentPage.current = 1;
  }, [activeMainFilters]);

  const { fetchMore, previousData, loading, data, refetch, variables } =
    useBookingKitEventLog_GetEventsQuery({
      variables: {
        kitId: props.kitId,
        limit: PAGE_SIZE,
        skip: (currentPage.current - 1) * PAGE_SIZE,
        types: activeFilters as BookingKitEventType[]
      },
      fetchPolicy: "cache-and-network",
      onError: (e) => {
        console.error(e);
        snack.showError(locale.ErrorOccurred);
      },
      onCompleted: (data) => {
        setEventsGroupedByFilterType(
          groupEventsByFilterType(data.bookingKitEvents.filter((e) => !e.Deleted))
        );
      },
      notifyOnNetworkStatusChange: true
    });

  const eventLogData = useEventLogData(refetch, variables);

  const filteredEventComponents = createFilteredEventComponents({
    filters: activeFilters,
    events: eventsGroupedByFilterType,
    onUpdated: () => {
      eventLogData.refetch();
    }
  });

  const providerData = useMemo(
    () => ({
      refetch,
      variables
    }),
    [refetch, variables]
  );

  return (
    <EventLogDataProvider value={providerData}>
      <Box style={{ display: "flex", flexDirection: "column", rowGap: "1rem" }}>
        <Card style={{ minHeight: "500px" }}>
          <EventList
            isLoading={loading && !previousData}
            isInputLoading={false}
            eventComponents={filteredEventComponents}
            fetchMore={() => {
              setFetchMoreIsLoading(true);
              currentPage.current++;
              fetchMore({
                variables: {
                  skip: (currentPage.current - 1) * PAGE_SIZE
                }
              }).finally(() => setFetchMoreIsLoading(false));
            }}
            fetchMoreIsLoading={fetchMoreIsLoading}
            canFetchMore={
              data?.bookingKitEvents.length === currentPage.current * PAGE_SIZE
            }
          />
        </Card>
      </Box>
    </EventLogDataProvider>
  );
};
